import { type ReactNode, useMemo } from 'react';
import type { Creator, CreatorStatus } from '@/types';
import { useMinLoading } from '@/hooks/use-min-loading';
import { useColumnCreators } from '../hooks/use-column-creators';
import { KanbanCard } from './kanban-card';
import { ColumnSkeleton } from './kanban-skeleton';

type Props = {
  status: CreatorStatus;
  skeletonCount: number;
  onSelect: (id: string) => void;
};

function LoadMore({
  loading,
  onClick,
}: {
  loading: boolean;
  onClick: () => void;
}): ReactNode {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="w-full py-sm text-center text-[10px] text-text-tertiary hover:text-text-secondary"
    >
      {loading ? 'Cargando...' : 'Cargar más'}
    </button>
  );
}

function CardList({
  creators,
  onSelect,
}: {
  creators: Creator[];
  onSelect: (id: string) => void;
}): ReactNode {
  return (
    <>
      {creators.map((l: Creator) => (
        <KanbanCard key={l.id} creator={l} onClick={() => onSelect(l.id)} />
      ))}
    </>
  );
}

export function ColumnBody({
  status,
  skeletonCount,
  onSelect,
}: Props): ReactNode {
  const q = useColumnCreators(status);
  const showSkeleton = useMinLoading(q.isLoading);
  const creators = useMemo(
    () => q.data?.pages.flatMap((p) => p.items) ?? [],
    [q.data]
  );
  if (showSkeleton) return <ColumnSkeleton count={skeletonCount} />;
  return (
    <div className="animate-fade-in space-y-xs">
      <CardList creators={creators} onSelect={onSelect} />
      {q.hasNextPage && (
        <LoadMore
          loading={q.isFetchingNextPage}
          onClick={() => q.fetchNextPage()}
        />
      )}
    </div>
  );
}

export function useColumnCount(status: CreatorStatus): number {
  const { data } = useColumnCreators(status);
  return data?.pages[0]?.totalItems ?? 0;
}
